/**
 * SharedMessagesV2 — Universal messaging component
 * Uses useInternalChat (Firestore direct)
 * All roles: buyer, seller, broker, consultant, lender, partner, staff
 */
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useInternalChat } from '@/hooks/useInternalChat';
import { useMessageNotifications, clearTabBadge } from '@/hooks/useMessageNotifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MessageSquare,
  Send,
  Plus,
  Search,
  Loader2,
  Check,
  CheckCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const NAVY = '#1D314D';
const GOLD = '#CEAB65';

interface SystemContact {
  id: string;
  nameEn: string;
  nameFr: string;
  roleEn: string;
  roleFr: string;
  avatar: string;
}

const SYSTEM_CONTACTS: SystemContact[] = [
  {
    id: 'kUyMOvOh1faFiiutNfiV7hPVy2t2',
    nameEn: 'AcquizGroup Team',
    nameFr: 'Équipe AcquizGroup',
    roleEn: 'Platform Administration',
    roleFr: 'Administration plateforme',
    avatar: 'AQ',
  },
  {
    id: '5oYW8HpeZMbsREok3PfRxMIH6Hm2',
    nameEn: 'Technical Support',
    nameFr: 'Support Technique',
    roleEn: 'Technical Support',
    roleFr: 'Support technique',
    avatar: 'TS',
  },
];

const T = {
  en: {
    title: 'Messages',
    newMessage: 'New Message',
    search: 'Search conversations...',
    searchContacts: 'Search contacts...',
    noConversations: 'No conversations yet',
    noConversationsDesc: 'Start a conversation with AcquizGroup support.',
    selectConversation: 'Select a conversation',
    typeMessage: 'Type a message...',
    systemContacts: 'AcquizGroup Contacts',
    startConversation: 'Start conversation',
    cancel: 'Cancel',
    newConversation: 'New Conversation',
    selectContact: 'Select who you want to message',
    noMessages: 'No messages yet — say hello!',
    sendError: 'Send error',
    createError: 'Creation error',
  },
  fr: {
    title: 'Messages',
    newMessage: 'Nouveau message',
    search: 'Rechercher des conversations...',
    searchContacts: 'Rechercher des contacts...',
    noConversations: 'Aucune conversation',
    noConversationsDesc: 'Démarrez une conversation avec le support AcquizGroup.',
    selectConversation: 'Sélectionnez une conversation',
    typeMessage: 'Tapez un message...',
    systemContacts: 'Contacts AcquizGroup',
    startConversation: 'Démarrer la conversation',
    cancel: 'Annuler',
    newConversation: 'Nouvelle conversation',
    selectContact: 'Sélectionnez avec qui vous souhaitez communiquer',
    noMessages: 'Aucun message — soyez le premier!',
    sendError: "Erreur d'envoi",
    createError: 'Erreur de création',
  },
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatMsgTime(date: Date | string | undefined | null, lang: string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffHours = (new Date().getTime() - d.getTime()) / 3600000;
  if (diffHours < 24) return format(d, 'HH:mm');
  if (diffHours < 48) return lang === 'fr' ? 'Hier' : 'Yesterday';
  return format(d, 'dd MMM', { locale: lang === 'fr' ? fr : enUS });
}

interface Props {
  role?: string;
}

export default function SharedMessagesV2({ role: _role }: Props) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[(language as 'en' | 'fr')] ?? T.en;
  const dateLocale = language === 'fr' ? fr : enUS;

  const chat = useInternalChat() as any;
  const {
    conversations = [],
    selectedConversation: activeConv,
    messages = [],
    loadingConversations: loading,
    loadingMessages: messagesLoading,
    sending,
    selectConversation,
    sendMessage,
    createConversation,
  } = chat;

  const [searchTerm, setSearchTerm] = useState('');
  const [newMsg, setNewMsg] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<SystemContact | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link ?to=<uid>
  useEffect(() => {
    const toUid = searchParams.get('to');
    if (!toUid || loading) return;
    const existing = conversations.find((c: any) =>
      c.participants?.some((p: any) =>
        (typeof p === 'string' ? p : p.userId) === toUid,
      ),
    );
    if (existing) {
      selectConversation?.(existing);
    } else {
      createConversation?.({ participantIds: [toUid] }).catch(console.error);
    }
    setSearchParams({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversations, loading]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

 // Real-time notifications
  useMessageNotifications();
  // Clear tab badge when a conversation is open
  useEffect(() => {
    if (activeConv) clearTabBadge();
  }, [activeConv]);


  const totalUnread = conversations.reduce(
    (sum: number, c: any) => sum + (c.unreadCount || 0),
    0,
  );

  const filteredConversations = conversations.filter((c: any) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (c.title || '').toLowerCase().includes(s) ||
      (c.lastMessage || '').toLowerCase().includes(s)
    );
  });

  const getConvTitle = (conv: any): string => {
    if (conv?.title) return conv.title;
    const other = conv?.participants?.find((p: any) => p.userId !== user?.id);
    return other?.userName || 'Conversation';
  };

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    try {
      await sendMessage?.({ content: newMsg.trim() });
      setNewMsg('');
    } catch {
      toast.error(t.sendError);
    }
  };

  const handleCreateConversation = async () => {
    if (!selectedContact) return;
    try {
      await createConversation?.({
        participantIds: [selectedContact.id],
        title:
          language === 'fr' ? selectedContact.nameFr : selectedContact.nameEn,
      });
      setShowNewDialog(false);
      setSelectedContact(null);
      setContactSearch('');
    } catch {
      toast.error(t.createError);
    }
  };

  const filteredContacts = SYSTEM_CONTACTS.filter(
    (c) =>
      !contactSearch ||
      c.nameEn.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.nameFr.toLowerCase().includes(contactSearch.toLowerCase()),
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Compact header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          {t.title}
          {totalUnread > 0 && (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: GOLD, color: NAVY }}
            >
              {totalUnread}
            </span>
          )}
        </h2>
        <Button
          size="sm"
          onClick={() => setShowNewDialog(true)}
          style={{
            background: NAVY,
            color: GOLD,
            border: '1px solid rgba(206,171,101,0.3)',
          }}
        >
          <Plus className="w-4 h-4 mr-1" />
          {t.newMessage}
        </Button>
      </div>

      {/* Chat layout */}
      <div
        className="flex-1 min-h-[500px] grid border rounded-lg overflow-hidden bg-background"
        style={{ gridTemplateColumns: '300px 1fr' }}
      >
        {/* Sidebar */}
        <div className="flex flex-col border-r min-h-0">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t.search}
                className="pl-8 h-8 text-xs"
              />
            </div>
          </div>

          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <MessageSquare className="w-8 h-8 text-muted-foreground/50 mb-2" />
                <p className="text-xs font-medium">{t.noConversations}</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t.noConversationsDesc}
                </p>
              </div>
            ) : (
              filteredConversations.map((conv: any) => {
                const isActive = conv.id === activeConv?.id;
                const unread = conv.unreadCount || 0;
                const title = getConvTitle(conv);
                const lastMsgTime = conv.lastMessageAt
                  ? formatMsgTime(conv.lastMessageAt, language)
                  : '';
                return (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation?.(conv)}
                    className="w-full text-left transition-colors hover:bg-muted/40"
                    style={{
                      padding: '10px 12px',
                      borderBottom: '0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.06))',
                      background: isActive ? 'rgba(29,49,77,0.06)' : 'transparent',
                      borderLeft: isActive
                        ? `2px solid ${GOLD}`
                        : '2px solid transparent',
                    }}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="relative shrink-0">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold"
                          style={{ background: NAVY, color: GOLD }}
                        >
                          {getInitials(title)}
                        </div>
                        {unread > 0 && (
                          <span
                            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                            style={{ background: GOLD, color: NAVY }}
                          >
                            {unread > 9 ? '9+' : unread}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className="text-xs truncate"
                            style={{ fontWeight: unread > 0 ? 600 : 500 }}
                          >
                            {title}
                          </span>
                          {lastMsgTime && (
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              {lastMsgTime}
                            </span>
                          )}
                        </div>
                        {conv.lastMessage && (
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                            {conv.lastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </ScrollArea>
        </div>

        {/* Messages area */}
        <div className="flex flex-col min-h-0">
          {!activeConv ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(29,49,77,0.06)' }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: NAVY }} />
              </div>
              <p className="text-sm text-muted-foreground">
                {t.selectConversation}
              </p>
              <Button
                size="sm"
                onClick={() => setShowNewDialog(true)}
                style={{
                  background: NAVY,
                  color: GOLD,
                  border: '1px solid rgba(206,171,101,0.3)',
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t.newMessage}
              </Button>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="flex items-center gap-2.5 px-4 py-3 border-b">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold"
                  style={{ background: NAVY, color: GOLD }}
                >
                  {getInitials(getConvTitle(activeConv))}
                </div>
                <p className="text-sm font-medium truncate">
                  {getConvTitle(activeConv)}
                </p>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1">
                <div className="p-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10">
                      <MessageSquare className="w-8 h-8 text-muted-foreground/50 mb-2" />
                      <p className="text-xs text-muted-foreground">
                        {t.noMessages}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg: any, idx: number) => {
                        const isOwn = msg.fromUserId === user?.id;
                        const isRead =
                          Array.isArray(msg.readBy) && msg.readBy.length > 1;
                        const prevMsg = messages[idx - 1];
                        const showDate =
                          !prevMsg ||
                          format(new Date(msg.createdAt), 'yyyy-MM-dd') !==
                            format(new Date(prevMsg.createdAt), 'yyyy-MM-dd');
                        return (
                          <div key={msg.id}>
                            {showDate && (
                              <div className="flex items-center gap-2 my-3">
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-[10px] text-muted-foreground px-2">
                                  {format(new Date(msg.createdAt), 'dd MMMM yyyy', {
                                    locale: dateLocale,
                                  })}
                                </span>
                                <div className="flex-1 h-px bg-border" />
                              </div>
                            )}
                            <div
                              className={`flex gap-2 ${
                                isOwn ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              {!isOwn && (
                                <div
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0"
                                  style={{ background: NAVY, color: GOLD }}
                                >
                                  {getInitials(msg.fromUserName || '?')}
                                </div>
                              )}
                              <div
                                className={`max-w-[70%] flex flex-col ${
                                  isOwn ? 'items-end' : 'items-start'
                                }`}
                              >
                                {!isOwn && (
                                  <span className="text-[10px] text-muted-foreground mb-0.5">
                                    {msg.fromUserName}
                                  </span>
                                )}
                                <div
                                  className="rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words"
                                  style={{
                                    background: isOwn ? NAVY : 'var(--muted, #f4f4f5)',
                                    color: isOwn ? GOLD : 'inherit',
                                  }}
                                >{msg.content}
                                  {msg.originalContent && (
                                    <div
                                      className="mt-1 pt-1 border-t border-white/10 text-[10px] italic opacity-70"
                                      title={msg.originalContent}
                                    >
                                      {language === 'fr' ? 'Traduit automatiquement' : 'Auto-translated'}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[9px] text-muted-foreground">
                                    {format(new Date(msg.createdAt), 'HH:mm')}
                                  </span>
                                  {isOwn &&
                                    (isRead ? (
                                      <CheckCheck className="w-3 h-3" style={{ color: GOLD }} />
                                    ) : (
                                      <Check className="w-3 h-3 text-muted-foreground" />
                                    ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="border-t p-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={t.typeMessage}
                    className="flex-1 text-sm"
                    disabled={sending}
                  />
                  <Button
                    size="sm"
                    onClick={handleSend}
                    disabled={!newMsg.trim() || sending}
                    style={{ background: NAVY, color: GOLD }}
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.newConversation}</DialogTitle>
            <p className="text-xs text-muted-foreground">{t.selectContact}</p>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder={t.searchContacts}
                className="pl-8 text-sm"
              />
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {t.systemContacts}
              </p>
              <div className="space-y-1.5">
                {filteredContacts.map((contact) => {
                  const isSelected = selectedContact?.id === contact.id;
                  return (
                    <button
                      key={contact.id}
                      onClick={() =>
                        setSelectedContact(isSelected ? null : contact)
                      }
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors text-left"
                      style={{
                        background: isSelected
                          ? 'rgba(29,49,77,0.08)'
                          : 'transparent',
                        border: isSelected
                          ? `1px solid ${GOLD}`
                          : '1px solid transparent',
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
                        style={{ background: NAVY, color: GOLD }}
                      >
                        {contact.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {language === 'fr' ? contact.nameFr : contact.nameEn}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {language === 'fr' ? contact.roleFr : contact.roleEn}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowNewDialog(false);
                  setSelectedContact(null);
                  setContactSearch('');
                }}
              >
                {t.cancel}
              </Button>
              <Button
                size="sm"
                onClick={handleCreateConversation}
                disabled={!selectedContact}
                style={{ background: NAVY, color: GOLD }}
              >
                {t.startConversation}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
